<template>
  <div>
    <v-layout row wrap>
      <v-menu
        v-model="menuStart"
        :close-on-content-click="false"
        :nudge-right="40"
        lazy
        transition="scale-transition"
        offset-y
        full-width
        min-width="290px"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-model="startDate"
            :disabled="disabled || disabledStart"
            :class="{ 'fix-date-width': fixDateWidth }"
            v-on="on"
            label="開始日"
            prepend-icon="event"
            readonly
          />
        </template>
        <v-date-picker
          v-model="startDate"
          :allowed-dates="allowedDatesOfStart"
          :day-format="formatDate"
          locale="ja"
        />
      </v-menu>
      <v-select
        v-if="hourSelectable"
        v-model="startHour"
        :items="startHourList"
        :disabled="!startDate || disabled || disabledStart"
        class="ml-2 hourSelector"
      />
      <div class="dash pa-2">-</div>
      <v-menu
        v-model="menuEnd"
        :close-on-content-click="false"
        :nudge-right="40"
        lazy
        transition="scale-transition"
        offset-y
        full-width
        min-width="290px"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-model="endDate"
            :clearable="clearable"
            :disabled="startDate === null"
            :class="{ 'fix-date-width': fixDateWidth }"
            v-on="on"
            label="終了日"
            prepend-icon="event"
            readonly
          />
        </template>
        <v-date-picker
          v-model="endDate"
          :allowed-dates="allowedDatesOfEnd"
          :day-format="formatDate"
          locale="ja"
        />
      </v-menu>
      <v-select
        v-if="hourSelectable"
        v-model="endHour"
        :items="endHourList"
        :disabled="!endDate || disabled"
        class="ml-2 hourSelector"
      />
    </v-layout>
  </div>
</template>

<script>
import dayjs from 'dayjs'

const dateFormat = 'YYYY-MM-DD'

export default {
  name: 'AppFormPeriodPicker',
  props: {
    start: {
      type: String,
      default: '',
    },
    end: {
      type: String,
      default: '',
    },
    clearable: { type: Boolean, default: false },
    disabled: Boolean,
    disabledAlreadyStart: Boolean,
    hourSelectable: Boolean,
    usableStartDateFilter: {
      type: Function,
      default: null,
    },
    usableEndDateFilter: {
      type: Function,
      default: null,
    },
    isDateRestrictions: {
      type: Boolean,
      default: true,
    },
    isStartDateLimit: {
      type: Boolean,
      default: true,
    },
    fixDateWidth: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      menuStart: false,
      menuEnd: false,
      initialStart: this.start,
      initialEnd: this.end,
    }
  },
  computed: {
    startDate: {
      get() {
        if (!this.start) return null
        return dayjs(this.start).format(dateFormat)
      },
      set(value) {
        this.menuStart = false
        this.$emit(
          'inputStartDate',
          this.format(value, { hour: this.startHour, type: 'start' }),
        )
      },
    },
    endDate: {
      get() {
        if (!this.end) return null
        return dayjs(this.end).format(dateFormat)
      },
      set(value) {
        this.menuEnd = false
        this.$emit(
          'inputEndDate',
          this.format(value, { hour: this.endHour, type: 'end' }),
        )
      },
    },
    startHour: {
      get() {
        if (!this.start) return null
        return dayjs(this.start).hour()
      },
      set(value) {
        this.$emit(
          'inputStartDate',
          this.format(this.startDate, { hour: value, type: 'start' }),
        )
      },
    },
    endHour: {
      get() {
        if (!this.end) return null
        return dayjs(this.end).hour()
      },
      set(value) {
        this.$emit(
          'inputEndDate',
          this.format(this.endDate, { hour: value, type: 'end' }),
        )
      },
    },
    startHourList() {
      const times = []
      const maxHour = this.isSameDate && this.endHour ? this.endHour : 23
      for (let i = 0; i <= maxHour; i++) {
        times.push({ text: `${i}:00`, value: i })
      }
      return times
    },
    endHourList() {
      const times = []
      const initialHour = this.isSameDate && this.startHour ? this.startHour : 0
      for (let i = initialHour; i < 24; i++) {
        times.push({ text: `${i}:59`, value: i })
      }
      return times
    },
    isSameDate() {
      return this.startDate === this.endDate
    },
    /**
     * すでに開始しているかどうか
     */
    alreadyStarted() {
      return this.initialStart && dayjs().isAfter(this.initialStart)
    },
    disabledStart() {
      return this.disabledAlreadyStart && this.alreadyStarted
    },
  },
  methods: {
    allowedDates(date) {
      if (this.isStartDateLimit) {
        // 本日 ~ 終了日まで選択可
        return (
          (dayjs(date).isAfter(dayjs(), 'day') ||
            dayjs(date).isSame(dayjs(), 'day')) &&
          this.allowedStartDatesByPermitter(date)
        )
      } else {
        // 過去 ~ 終了日まで選択可
        return true
      }
    },
    allowedStartDatesByPermitter(date) {
      return this.usableStartDateFilter == null
        ? true
        : this.usableStartDateFilter(date)
    },
    allowedEndDatesByPermitter(date, startDate) {
      return this.usableEndDateFilter == null
        ? true
        : this.usableEndDateFilter(date, startDate)
    },
    allowedDatesOfStart(date) {
      if (this.isDateRestrictions) {
        return this.endDate
          ? (this.allowedDates(date) &&
              this.allowedStartDatesByPermitter(date) &&
              dayjs(date).isBefore(this.endDate)) ||
              dayjs(date).isSame(this.endDate)
          : this.allowedDates(date)
      } else {
        return true
      }
    },
    allowedDatesOfEnd(date) {
      const base = this.startDate
        ? this.disabledStart
          ? dayjs()
              .startOf('day')
              .format(dateFormat)
          : this.startDate
        : null
      return base
        ? (dayjs(date).isAfter(base) || dayjs(date).isSame(base)) &&
            this.allowedDates(date) &&
            this.allowedEndDatesByPermitter(date, base)
        : this.allowedDates(date)
    },
    formatDate(date) {
      return dayjs(date).format('D')
    },
    format(date, { hour = undefined, type = 'start' } = {}) {
      if (date == null) return null
      if (this.hourSelectable) {
        return dayjs(date)
          .hour(hour)
          .format(`${dateFormat} HH:${type === 'start' ? '00:00' : '59:59'}`)
      } else {
        return dayjs(date).format(dateFormat)
      }
    },
  },
}
</script>

<style scoped lang="scss">
.dash {
  align-self: center;
}
.hourSelector {
  width: 60px;
}
.fix-date-width {
  max-width: 160px;
}
</style>
